import type { PlayerTrack } from '@/components/player/PlayerProvider'
import { lookupTrack, songTrack } from '@/components/player/tracks'

// The four kinds of song the builder deals from (see Peloton::PlaylistPool).
export type Tag = 'staple' | 'gem' | 'fresh' | 'essential'
export const TAGS: Tag[] = ['staple', 'gem', 'fresh', 'essential']
// The two kinds that are new to the instructor -- what the dial turns up.
export const NEW_TAGS: Tag[] = ['fresh', 'essential']

export interface BuildItem {
  key: string
  kind: 'song' | 'essential'
  tag: Tag
  id: string | null
  title: string
  artists: string[]
  image_url: string | null
  plays: number
  peer_plays: number
  rank: number | null
  // Where in a ride this song usually sits (0 = opener, 1 = closer); null when
  // there isn't enough history to say.
  avg_position: number | null
}

export interface ServerSlot {
  index: number
  role: string
  starts_at: number
  ends_at: number
  locked: boolean
  pick: BuildItem | null
  alternatives: BuildItem[]
}

export interface BuildResponse {
  seed: number
  minutes: number
  song_count: number
  rides_considered: number
  pool: Partial<Record<Tag, number>>
  slots: ServerSlot[]
}

export interface DisciplineOption {
  key: string
  rides: number
  durations: { minutes: number; rides: number }[]
  formats: { id: string; name: string; rides: number }[]
  themes: { name: string; rides: number }[]
}

export interface PlaylistOptions {
  instructor: { id: string; name: string; image_url: string | null }
  disciplines: DisciplineOption[]
}

export const TAG_META: Record<Tag, { label: string; short: string; color: string; blurb: (name: string) => string }> = {
  staple: { label: 'Staple', short: 'Staples', color: '#2a78d6', blurb: (n) => `${n} plays this all the time` },
  gem: { label: 'Hidden gem', short: 'Gems', color: '#8b6cf0', blurb: (n) => `${n} has played this once or twice` },
  fresh: { label: 'Fresh for them', short: 'Fresh', color: '#eb6834', blurb: (n) => `Other instructors play it -- ${n} never has` },
  essential: { label: 'Never-played essential', short: 'Essentials', color: '#e87ba4', blurb: (n) => `A Last.fm favourite ${n} has never played` },
}

// A song we know (in the catalog) previews by id; an essential by artist + title.
export function itemTrack(item: BuildItem): PlayerTrack {
  return item.kind === 'essential' || !item.id
    ? lookupTrack(item.artists[0] ?? '', item.title)
    : songTrack({ id: item.id, title: item.title, artists: item.artists, album_image_url: item.image_url })
}

// How well a song sits where it's been put: 1 = exactly where it usually
// goes in a ride, 0 = the opposite end. null when we have no reliable habit
// to compare against.
export function fitFor(item: BuildItem | null, index: number, count: number): number | null {
  if (!item || item.avg_position == null) return null
  const relative = count <= 1 ? 0.5 : index / (count - 1)
  return Math.max(0, 1 - Math.abs(item.avg_position - relative))
}

export function fitLabel(fit: number): { text: string; good: boolean } {
  if (fit >= 0.85) return { text: 'Right where it belongs', good: true }
  if (fit >= 0.65) return { text: 'Works here', good: true }
  return { text: 'Usually goes elsewhere', good: false }
}

export function formatClock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

export const DIAL_STOPS = [
  { upTo: 0.15, label: 'Comfort zone', blurb: (n: string) => `Almost all songs ${n} already plays.` },
  { upTo: 0.4, label: 'Familiar-ish', blurb: (n: string) => `Mostly ${n}'s staples, with a few fresh faces.` },
  { upTo: 0.65, label: 'Curious', blurb: (n: string) => `An even split of ${n}'s old favourites and new finds.` },
  { upTo: 0.88, label: 'Adventurous', blurb: (n: string) => `Mostly songs ${n} has never played.` },
  { upTo: 1.01, label: 'Uncharted', blurb: (n: string) => `Nothing ${n} plays -- all of it new to them.` },
]

export function dialStop(adventure: number) {
  return DIAL_STOPS.find((stop) => adventure < stop.upTo) ?? DIAL_STOPS[DIAL_STOPS.length - 1]
}

interface SetlistRow {
  item: BuildItem | null
  role: string
  startsAt: number
}

// A plain-text setlist: paste it anywhere.
export function setlistText(title: string, rows: SetlistRow[]): string {
  const lines = rows
    .filter((row): row is SetlistRow & { item: BuildItem } => row.item !== null)
    .map((row, i) => `${String(i + 1).padStart(2, '0')}  ${formatClock(row.startsAt)}  ${row.item.artists.join(', ')} – ${row.item.title}  (${row.role})`)
  return [title, '', ...lines].join('\n')
}

export function randomSeed() {
  return Math.floor(Math.random() * 1_000_000)
}
